import { Box } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- billing table uses bare DataGridPro for client-side totals + pinned-row footer; toolbar uses shared DataGridToolbar
import { DataGridPro, gridFilteredSortedRowIdsSelector, useGridApiRef } from '@mui/x-data-grid-pro';
import { addMonths, endOfDay, format, startOfDay } from 'date-fns';
import { fromJS } from 'immutable';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import DataGridToolbar from '@sophocles/data-grids/components/toolbar';
import { fetchTotalsByCrewService } from '@sophocles/entities/ServicePerformed';
import { getTotalReducer } from '@sophocles/features/projectServices/utils';
import { formatNumberWithCommas } from '@sophocles/utils';

import InstallServicesBillingExcelButton from './InstallServicesBillingExcelButton';

const getRowIdForRow = row => row.id ?? `${_.get(row, 'crew.id', '')}_${_.get(row, 'service.id', '')}`;

function CrewBillingTable({
  customerId,
  projectId,
  onChangeDateRange,
  startDate: externalStart,
  endDate: externalEnd,
  extraParams,
  extraToolbarContent,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const apiRef = useGridApiRef();

  const [internalStart, setInternalStart] = useState(() => startOfDay(addMonths(new Date(), -1)));
  const [internalEnd, setInternalEnd] = useState(() => endOfDay(new Date()));

  const startDate = externalStart || internalStart;
  const endDate = externalEnd || internalEnd;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredCount, setFilteredCount] = useState(null);

  useEffect(() => {
    setLoading(true);
    dispatch(
      fetchTotalsByCrewService({
        service_date__gte: format(startDate, 'yyyy-MM-dd'),
        service_date__lte: format(endDate, 'yyyy-MM-dd'),
        ...extraParams,
      }),
    )
      .then(res => {
        const data = res?.results || res?.payload?.results || (Array.isArray(res) ? res : []);
        setRows(data);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [dispatch, startDate, endDate, extraParams]);

  const handleStateChange = useCallback(() => {
    if (!apiRef.current) return;
    const ids = gridFilteredSortedRowIdsSelector(apiRef);
    setFilteredCount(prev => (ids.length === prev ? prev : ids.length));
  }, []);

  const visibleRows = useMemo(() => {
    if (!apiRef.current || rows.length === 0) return rows;
    const ids = gridFilteredSortedRowIdsSelector(apiRef);
    if (ids.length === rows.length) return rows;
    const idSet = new Set(ids.map(String));
    return rows.filter(row => idSet.has(String(getRowIdForRow(row))));
  }, [rows, filteredCount]);

  const handleDateRangeChange = useCallback(
    (start, end) => {
      setInternalStart(start);
      setInternalEnd(end);
      if (onChangeDateRange) onChangeDateRange(start, end);
    },
    [onChangeDateRange],
  );

  useEffect(() => {
    if (onChangeDateRange) onChangeDateRange(startDate, endDate);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'crew',
        headerName: t('projectServices.billing.columns.crew'),
        flex: 1,
        minWidth: 200,
        disableColumnMenu: true,
        valueGetter: value =>
          _.get(value, 'crew_number') +
          '. ' +
          _.get(value, 'users', [])
            .map(user =>
              user
                ? _.get(user, 'first_name', '') +
                  (_.get(user, 'last_name', '') !== '' ? ' ' + _.get(user, 'last_name', '') : '')
                : t('projectServices.billing.invalidUser'),
            )
            .join(', '),
      },
      {
        field: 'service',
        headerName: t('projectServices.billing.columns.service'),
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
        valueGetter: value => _.get(value, 'name', t('projectServices.billing.noServiceTitle')),
      },
      {
        field: 'quantity',
        headerName: t('projectServices.billing.columns.qty'),
        width: 80,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
      },
      {
        field: 'total_price_charged_to_customer',
        headerName: t('projectServices.billing.columns.revenue'),
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value),
      },
      {
        field: 'total_price_paid_to_crew',
        headerName: t('projectServices.billing.columns.installerCost'),
        width: 130,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value),
      },
      {
        field: 'total_extra_paid_to_crew',
        headerName: t('projectServices.billing.columns.installerCostExtra'),
        width: 160,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value),
      },
      {
        field: 'profit',
        headerName: t('projectServices.billing.columns.profit'),
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value),
      },
    ],
    [t],
  );

  const revenue = visibleRows.reduce(getTotalReducer('total_price_charged_to_customer'), 0);
  const cost = visibleRows.reduce(getTotalReducer('total_price_paid_to_crew'), 0);
  const extraCost = visibleRows.reduce(getTotalReducer('total_extra_paid_to_crew'), 0);
  const profit = visibleRows.reduce(getTotalReducer('profit'), 0);

  const pinnedRows = useMemo(
    () => ({
      bottom: [
        {
          id: '__totals__',
          crew: { crew_number: t('projectServices.billing.totals'), users: [] },
          service: { name: '' },
          quantity: null,
          total_price_charged_to_customer: revenue,
          total_price_paid_to_crew: cost,
          total_extra_paid_to_crew: extraCost,
          profit,
        },
      ],
    }),
    [revenue, cost, extraCost, profit, t],
  );

  const excelColumns = fromJS([
    {
      id: 'crew',
      name: t('projectServices.billing.columns.crew'),
      cellRenderer: value =>
        _.get(value, 'crew_number') +
        '. ' +
        _.get(value, 'users', [])
          .map(user =>
            user
              ? _.get(user, 'first_name', '') +
                (_.get(user, 'last_name', '') !== '' ? ' ' + _.get(user, 'last_name', '') : '')
              : t('projectServices.billing.invalidUser'),
          )
          .join(', '),
    },
    {
      id: 'service',
      name: t('projectServices.billing.columns.service'),
      cellRenderer: value => _.get(value, 'name', t('projectServices.billing.noServiceTitle')),
    },
    { id: 'quantity', name: t('projectServices.billing.columns.qty') },
    {
      id: 'total_price_charged_to_customer',
      name: t('projectServices.billing.columns.revenue'),
      cellRenderer: value => '$' + formatNumberWithCommas(value),
    },
    {
      id: 'total_price_paid_to_crew',
      name: t('projectServices.billing.columns.installerCost'),
      cellRenderer: value => '$' + formatNumberWithCommas(value),
    },
    {
      id: 'total_extra_paid_to_crew',
      name: t('projectServices.billing.columns.installerCostExtra'),
      cellRenderer: value => '$' + formatNumberWithCommas(value),
    },
    {
      id: 'profit',
      name: t('projectServices.billing.columns.profit'),
      cellRenderer: value => '$' + formatNumberWithCommas(value),
    },
  ]);

  const excelFooter = [
    t('projectServices.billing.totals'),
    '',
    '',
    '$' + formatNumberWithCommas(revenue),
    '$' + formatNumberWithCommas(cost),
    '$' + formatNumberWithCommas(extraCost),
    '$' + formatNumberWithCommas(profit),
  ];

  const exportButton = (
    <InstallServicesBillingExcelButton
      title={`billing_${format(startDate, 'yyyy-MM-dd')}_to_${format(endDate, 'yyyy-MM-dd')}_crew_summary`}
      firstLine={t('projectServices.billing.excelTitleCrew')}
      startDate={format(startDate, 'yyyy-MM-dd')}
      endDate={format(endDate, 'yyyy-MM-dd')}
      columns={excelColumns}
      data={fromJS(rows)}
      footer={excelFooter}
    />
  );

  const showDatePicker = !externalStart;

  const toolbarContent =
    extraToolbarContent ||
    (showDatePicker ? (
      <TimeRangeSelector
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateRangeChange}
        size="sm"
        exclude={['last1h', 'last24h']}
        defaultPreset="last30d"
      />
    ) : null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DataGridToolbar apiRef={apiRef} showQuickFilter toolbarContent={toolbarContent} toolbarActions={exportButton} />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={getRowIdForRow}
        pinnedRows={rows.length > 0 ? pinnedRows : undefined}
        onStateChange={handleStateChange}
        initialState={{
          sorting: { sortModel: [{ field: 'crew', sort: 'asc' }] },
        }}
        disableColumnFilter
        disableColumnSelector
        disableRowSelectionOnClick
        density="compact"
        getRowHeight={() => 'auto'}
        getRowClassName={params => (params.row.id === '__totals__' ? 'totals-row' : '')}
        sx={{
          flex: 1,
          minHeight: 0,
          '& .MuiDataGrid-pinnedRows': {
            bgcolor: 'var(--grid-header-bg)',
            borderTop: '1px solid var(--grid-header-border)',
          },
          '& .totals-row': { fontWeight: 'bold' },
        }}
      />
    </Box>
  );
}

export default CrewBillingTable;
